import { useTranslation } from 'react-i18next';
import { useState } from 'react';
import DeliveryLocationTable from './tables/DeliveryLocationTable';
import { RetailerDeliveryLocationModel } from '../../../../app/services/api/ofdClient';
import CreateEditModal from './modals/DeliveryLocationEditCreateModal';
import PageArea from '../../../../components/shared/PageArea';
import MessagesSnackbar from '../../../../components/shared/MessagesSnackbar';
import { Box } from '@mui/material';
import { Section } from '../../../../components/ui/Section';
import { ConfirmationDialog } from '../../../../components/shared/dialogs/ConfirmationDialog';
import { Trash01 } from '../../../../assets';

interface OnboardingDeliveryLocationsProps {
  formData: RetailerDeliveryLocationModel[],
  handleEditLocation: Function,
  handleDeleteLocation: Function,
  isLoading: boolean,
  setIsFormDirty: Function,
}

const defaultLocation: RetailerDeliveryLocationModel = {
  id: 0,
  name: '',
  contactName: '',
  contactNumber: '',
  addressLine1: '',
  addressLine2: '',
  city: '',
  postalCode: '',
  country: '',
};


const OnboardingDeliveryLocations: React.FC<OnboardingDeliveryLocationsProps> = ({ formData, handleEditLocation, handleDeleteLocation, isLoading, setIsFormDirty }) => {
  const { t } = useTranslation('onboardingWizard');
  const [openEditModal, setOpenEditModal] = useState(false);
  const [deleteModalOpen, setDeleteModalOpen] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState<RetailerDeliveryLocationModel>(defaultLocation);
  const [showDeleteMessage, setShowDeleteMessage] = useState<boolean>(false);
  const [showAddedMessage, setShowAddedMessage] = useState<boolean>(false);
  const [showUpdatedMessage, setShowUpdatedMessage] = useState<boolean>(false);

  const onCloseEditModal = () => {
    setOpenEditModal(false);
    setSelectedLocation({ ...defaultLocation });
  };

  const onCloseDeleteModal = () => {
    setDeleteModalOpen(false);
    setSelectedLocation({ ...defaultLocation });
  };

  const onOpenEditModal = (location?: RetailerDeliveryLocationModel) => {
    setOpenEditModal(true);
    setSelectedLocation(location ?? { ...defaultLocation });
  };

  const onOpenDeleteModal = (event: React.MouseEvent<HTMLButtonElement, MouseEvent>, location: RetailerDeliveryLocationModel) => {
    event.stopPropagation();
    setDeleteModalOpen(true);
    setSelectedLocation(location);
  };

  const handleDelete = () => {
    handleDeleteLocation(selectedLocation.id);
    setIsFormDirty(true);
    onCloseDeleteModal();
    setShowDeleteMessage(true);
  };

  const handleEdit = (location: RetailerDeliveryLocationModel) => {
    handleEditLocation(location);
    if (location.id && location.id > 0) {
      setShowUpdatedMessage(true);
    }
    setShowAddedMessage(true);
    setIsFormDirty(true);
  };

  return (
    <>
      <PageArea>
        <Box display="flex"
          width="100%"
          height="100%"
          flexDirection="column" >
          <Section>
            {
              <DeliveryLocationTable list={formData}
                openEditModal={openEditModal}
                onCloseEditModal={() => onCloseEditModal()}
                onOpenDeleteModal={(event: React.MouseEvent<HTMLButtonElement, MouseEvent>, location: RetailerDeliveryLocationModel) => onOpenDeleteModal(event, location)}
                onOpenEditModal={(val: RetailerDeliveryLocationModel) => onOpenEditModal(val)}
                selectedLocation={selectedLocation}
                isLoading={isLoading} />
            }
          </Section >
        </Box >
        <MessagesSnackbar open={showDeleteMessage}
          onClose={() => { setShowDeleteMessage(false); }}
          message={t('deliveryLocationsTab.deletedMessage')}
          severity='success'
          duration={5000} />
        <MessagesSnackbar open={showAddedMessage}
          onClose={() => { setShowAddedMessage(false); }}
          message={t('deliveryLocationsTab.createdMessage')}
          severity='success'
          duration={5000} />
        <MessagesSnackbar
          open={showUpdatedMessage}
          onClose={() => setShowUpdatedMessage(false)}
          message={t('deliveryLocationsTab.updatedMessage')}
          severity="success"
          duration={5000}
        />
      </PageArea >

      <ConfirmationDialog open={deleteModalOpen}
        onCancel={() => onCloseDeleteModal()}
        onConfirm={() => handleDelete()}
        icon={<Trash01 />}
        title={t('deliveryLocationsTab.deleteModal.confirm')}
        message={t('deliveryLocationsTab.deleteModal.confirmMessage')}
        confirmButtonText={t('deliveryLocationsTab.deleteModal.yes')}
        cancelButtonText={t('deliveryLocationsTab.deleteModal.no')} />

      <CreateEditModal openModal={openEditModal}
        selectedLocation={selectedLocation}
        onCancel={onCloseEditModal}
        onConfirm={(location: RetailerDeliveryLocationModel) => {
          handleEdit(location);
          onCloseEditModal();
        }} />
    </>

  );
};

export default OnboardingDeliveryLocations;
